import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout,
  ManageAccounts,
  SettingsApplications,
  Inventory as InventoryIcon,
  Engineering as EngineeringIcon,
  CloudUpload as CloudUploadIcon,
  Router as RouterIcon,
  MenuBook as MenuBookIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      adminOnly: false
    },
    {
      text: 'Ordini',
      icon: <AssignmentIcon />,
      path: '/orders',
      adminOnly: false
    },
    {
      text: 'Clienti',
      icon: <BusinessIcon />,
      path: '/customers',
      adminOnly: false
    },
    {
      text: 'Prodotti',
      icon: <InventoryIcon />,
      path: '/products',
      adminOnly: false
    },
    {
      text: 'Macchine',
      icon: <EngineeringIcon />,
      path: '/machines',
      adminOnly: true
    },
    {
      text: 'Edge Gateway',
      icon: <RouterIcon />,
      path: '/edge-gateways',
      adminOnly: true
    },
    {
      text: 'Import Ordini',
      icon: <CloudUploadIcon />,
      path: '/import',
      adminOnly: true
    },
    {
      text: 'Utenti',
      icon: <PeopleIcon />,
      path: '/users',
      adminOnly: true
    },
    {
      text: 'Config OPC UA',
      icon: <SettingsApplications />,
      path: '/opcua-config',
      adminOnly: true
    },
    {
      text: 'Guida Gateway',
      icon: <MenuBookIcon />,
      path: '/gateway-guide',
      adminOnly: true
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', backgroundColor: '#32373c', color: '#ffffff' }}>
      <Toolbar sx={{ px: 2, py: 1.5, minHeight: '72px !important', justifyContent: 'center' }}>
        <img
          src="/logo-bionen-white.png"
          alt="Bionen"
          style={{ height: 40, objectFit: 'contain' }}
        />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ px: 1, py: 1 }}>
        {menuItems
          .filter(item => !item.adminOnly || isAdmin())
          .map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton
              selected={location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: '8px',
                mx: 0.5,
                color: 'rgba(255,255,255,0.7)',
                '& .MuiListItemIcon-root': {
                  color: 'rgba(255,255,255,0.5)',
                  minWidth: 40,
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  color: '#ffffff',
                  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.8)' },
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(6, 147, 227, 0.2)',
                  color: '#42b0f5',
                  '& .MuiListItemIcon-root': { color: '#42b0f5' },
                  '&:hover': {
                    backgroundColor: 'rgba(6, 147, 227, 0.25)',
                  },
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))) ? 600 : 400 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          color: '#32373c',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: '#32373c' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1.1rem' }}>
            Sistema Gestione Ordini
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2, color: '#6b7280' }}>
              {user?.username}
              {isAdmin() && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: '#0693e3', fontWeight: 600 }}>
                  Admin
                </Typography>
              )}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{ color: '#32373c' }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <ListItemIcon>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: '#0693e3', fontSize: '0.75rem' }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemIcon>
                Profilo
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Migliori performance su mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: '#32373c', borderRight: 'none' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: '#32373c', borderRight: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;